import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './registrationcss/Register.css';
import { eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';
import { FaInfoCircle } from 'react-icons/fa';
import RegistrationProgressBar from '../../components/usercomponents/RegistrationProgressBar';
import accountInfoImg from '../../images/AccountInfo_IMG.png';
import logo from '../../images/auravit_logo.png';
import { userService } from "../../services/firestoreService";
import {
  commonPasswords,
  storeRegistrationPassword,
} from "../../services/passwordUtils";
import { getStoredUIC } from "../../services/uicService";

function AccountInformation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [isUsernameValidated, setIsUsernameValidated] = useState(false); // Track if username passed uniqueness check
  const [isSaving, setIsSaving] = useState(false);
  const [uniqueIdentifierCode, setUniqueIdentifierCode] = useState('');
  const [showUicModal, setShowUicModal] = useState(false);

  // Get firstName and lastName from localStorage since they're not stored in Firestore
  const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
  const firstName = storedDraft.firstName || location.state?.firstName || "";
  const lastName = storedDraft.lastName || location.state?.lastName || "";
  const suggestedName = `${firstName}.${lastName}`.toLowerCase().replace(/\s/g, '');
  
  // Determine if user is minor for progress bar
  let isMinor = location.state?.isMinor || false;
  if (!isMinor) {
    const reasonForAssistance = storedDraft.reasonForAssistance || [];
    isMinor = Array.isArray(reasonForAssistance) 
      ? reasonForAssistance.includes('minor')
      : reasonForAssistance === 'minor';
  }

  // Determine if user needs accompanist for progress bar
  const reasonForAssistance = storedDraft.reasonForAssistance || [];
  const needsAccompanist = Array.isArray(reasonForAssistance)
    ? reasonForAssistance.some(r => ['pwd', 'senior', 'nrnw', 'others'].includes(r))
    : ['pwd', 'senior', 'nrnw', 'others'].includes(reasonForAssistance);

  // Retrieve UIC from localStorage
  useEffect(() => {
    const storedUIC = getStoredUIC();
    if (storedUIC) {
      setUniqueIdentifierCode(storedUIC);
    }
  }, []);

  // Username functionality removed - no longer needed in registration flow

  // Username validation removed - no longer needed in registration flow

  /* ---------------- VALIDATORS ---------------- */

  const validateUsername = (username) => {
    if (!username.trim()) return "Username is required";
    if (username.length < 8) return "Minimum 8 characters";
    if (username.length > 50) return "Maximum 50 characters";
    const regex = /^[a-zA-Z0-9._@-]+$/;
    if (!regex.test(username)) return "Username contains invalid characters";
    return "";
  };

  const validatePassword = (password, username = '') => {
    if (!password) return "Password is required";
    if (password.length < 8 || password.length > 24) return "Password must be 8-24 characters";
    if (!/[A-Z]/.test(password)) return "At least 1 uppercase letter";
    if (!/[a-z]/.test(password)) return "At least 1 lowercase letter";
    if (!/[0-9]/.test(password)) return "At least 1 number";
    if (!/[!@#$%^&*]/.test(password)) return "At least 1 special character (!@#$%^&*)";
    if (/\s/.test(password)) return "No spaces allowed";
    if (username && password.toLowerCase() === username.toLowerCase()) return "Cannot match username";
    if (commonPasswords.includes(password)) return "Cannot be a common password";
    if (/(.)\1\1\1/.test(password)) return "No more than 3 repeated characters";
    return "";
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return "Confirm password is required";
    if (confirmPassword !== password) return "Passwords do not match";
    return "";
  };

  /* ---------------- FORM VALIDATION FUNCTION ---------------- */

  const validateForm = () => {
    const newErrors = {};

    // Validate password (no longer depends on username)
    const passwordErr = validatePassword(formData.password, '');
    if (passwordErr) newErrors.password = passwordErr;

    // Validate confirm password
    const confirmErr = validateConfirmPassword(formData.confirmPassword, formData.password);
    if (confirmErr) newErrors.confirmPassword = confirmErr;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------- LIVE INPUT HANDLER ---------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle username field - remove spaces and restrict characters
    if (name === 'username') {
      // Remove spaces automatically
      const cleanValue = value.replace(/\s/g, '');
      // Only allow letters, numbers, periods, underscores, hyphens, and @ signs
      const filteredValue = cleanValue.replace(/[^a-zA-Z0-9._@-]/g, '');
      
      if (filteredValue.length > 50) return;
      
      setFormData(prev => ({ ...prev, [name]: filteredValue }));
      
      setErrors(prev => {
        const updated = { ...prev };
        const err = validateUsername(filteredValue);
        err ? updated.username = err : delete updated.username;
        return updated;
      });
      return;
    }
    
    if ((name === 'password' || name === 'confirmPassword') && value.length > 24) return;

    setFormData(prev => ({ ...prev, [name]: value }));

    setErrors(prev => {
      const updated = { ...prev };
      if (name === 'password') {
        const err = validatePassword(value, formData.username);
        err ? updated.password = err : delete updated.password;
        const confirmErr = validateConfirmPassword(formData.confirmPassword, value);
        confirmErr ? updated.confirmPassword = confirmErr : delete updated.confirmPassword;
      }
      if (name === 'confirmPassword') {
        const err = validateConfirmPassword(value, formData.password);
        err ? updated.confirmPassword = err : delete updated.confirmPassword;
      }
      return updated;
    });
  };

  /* ---------------- PASSWORD STRENGTH ---------------- */

  useEffect(() => {
    let strength = 0;
    if (formData.password.length >= 8) strength++;
    if (/[A-Z]/.test(formData.password)) strength++;
    if (/[a-z]/.test(formData.password)) strength++;
    if (/[0-9]/.test(formData.password)) strength++;
    if (/[!@#$%^&*]/.test(formData.password)) strength++;
    setPasswordStrength(strength);
  }, [formData.password]);

  /* ---------------- CONTINUE HANDLER ---------------- */

  const handleContinue = async () => {
    // Validate form before proceeding
    if (!validateForm()) return;

    // Don't proceed if already saving or still checking username
    if (isSaving || isChecking) return;

    const userEmail = location.state?.email;
    const userSex = location.state?.sex;

    if (!userEmail) {
      alert("Session expired. Please restart from Step 1.");
      navigate('/');
      return;
    }

    setIsSaving(true);
    try {
      const storedDraft = JSON.parse(localStorage.getItem("registrationDraft") || "{}");
      
      const profileDraft = {
        ...storedDraft,
        ...location.state,
        registrationStep: 4,
      };

      storeRegistrationPassword(formData.password);
      localStorage.setItem("registrationDraft", JSON.stringify(profileDraft));

      // Always navigate to user verification after account setup
      setTimeout(() => {
        navigate('/user-verification', { state: { ...location.state, ...profileDraft, type: "registration" } });
      }, 300);
    } catch (err) {
      console.error("Registration draft save failed:", err);
      setErrors(prev => ({ ...prev, form: err.message || "Unable to continue." }));
      alert(err.message || "Unable to continue.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="login-logo-container">
        <img 
          src={logo} 
          alt="AuraVit" 
          className="login-logo"
          onClick={() => navigate("/")}
        />
      </div>
    <div className="register-container">
      <div className="register-card">
        <div className="left-panel">
          <img
            src={accountInfoImg}
            alt="Account setup"
            className="panel-image"
          />
        </div>
        <div className="right-panel">
          <div className="registration-header-left">
            <h1 className="create-account-title">Create Account</h1>
            <p className="wellness-subtitle">
              Set up your account for secure and easy access.
            </p>
          </div>

          <RegistrationProgressBar currentStep={isMinor || needsAccompanist ? 5 : 4} />

          <form onSubmit={(e) => e.preventDefault()}>
            {/* Form-level errors */}
            {errors.form && (
              <div className="form-error-alert">
                <p>{errors.form}</p>
              </div>
            )}

            {/* Unique Identifier Code */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="uniqueIdentifierCode">
                  Unique Identifier Code
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="uniqueIdentifierCode"
                    type="text"
                    name="uniqueIdentifierCode"
                    value={uniqueIdentifierCode}
                    readOnly
                    className="readonly-field"
                    placeholder="Generated automatically"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    className="eye-icon"
                    onClick={() => setShowUicModal(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    <FaInfoCircle
                      style={{
                        color: '#666',
                        fontSize: '16px',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#007bff'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                    />
                  </button>
                </div>
                <small className="helper-text" style={{ textAlign: 'left' }}>System-generated unique identifier</small>
              </div>
            </div>

            <div className="form-row">
              <div className="input-group password-container">
                <label htmlFor="password">
                  Password <span className="required">*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? 'error' : ''}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                    disabled={isSaving}
                    maxLength="24"
                    required
                  />
                  <button 
                    type="button" 
                    className="eye-icon" 
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSaving}
                  >
                    <IonIcon icon={showPassword ? eyeOutline : eyeOffOutline} />
                  </button>
                </div>
                {errors.password && <div className="field-error">{errors.password}</div>}
                <div className="password-strength">
                  <div className="password-strength-bar">
                    <span style={{ width: `${(passwordStrength / 5) * 100}%` }} />
                  </div>
                  <div className="password-strength-text">
                    Use 8 or more characters with a mix of letters, numbers & symbols.
                  </div>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="input-group password-container">
                <label htmlFor="confirmPassword">
                  Confirm Password <span className="required">*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={errors.confirmPassword ? 'error' : ''}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                    disabled={isSaving}
                    maxLength="24"
                    required
                  />
                  <button 
                    type="button" 
                    className="eye-icon" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isSaving}
                  >
                    <IonIcon icon={showConfirmPassword ? eyeOutline : eyeOffOutline} />
                  </button>
                </div>
                {errors.confirmPassword && <div className="field-error">{errors.confirmPassword}</div>}
              </div>
            </div>

            <button 
              type="button" 
              className="continue-btn" 
              onClick={handleContinue}
              disabled={
                isSaving || 
                isChecking || 
                Object.keys(errors).length > 0 ||
                !formData.password ||
                !formData.confirmPassword
              }
            >
              {isSaving ? 'Creating Account...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>

    {/* UIC Information Modal */}
    {showUicModal && (
      <div
        className="modal-overlay"
        onClick={() => setShowUicModal(false)}
      >
        <div
          className="modal-content uic-modal-wide"
          style={{ maxWidth: '1100px', width: '90%', padding: '2.5rem 3.5rem', textAlign: 'left' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <h3>Why Is a Unique Identifier Code (UIC) Needed?</h3>
            <button
              onClick={() => setShowUicModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
          <div style={{ lineHeight: '1.6', color: '#4a5568', textAlign: 'left', maxWidth: '100%' }}>
            <p style={{ marginBottom: '12px' }}>
              A <strong>Unique Identifier Code (UIC)</strong> is used during HIV testing to help protect your privacy and confidentiality. Instead of using your full name as the primary identifier, the UIC provides a secure way to associate your HIV testing information with your records.
            </p>
            <p style={{ marginBottom: '12px' }}>
              Your UIC is created using the following format:
            </p>
            <p style={{ marginBottom: '12px', fontStyle: 'italic', paddingLeft: '16px' }}>
              <strong>Mother's first name (first 2 letters) + Father's first name (first 2 letters) + Birth order + Date of birth</strong>
            </p>
            <p style={{ marginBottom: '12px' }}>
              <strong>Example:</strong>
            </p>
            <p style={{ marginBottom: '12px', paddingLeft: '16px' }}>
              Mother: Maria<br />
              Father: Juan<br />
              Birth order: 1st child<br />
              Date of birth: January 15, 2000
            </p>
            <p style={{ marginBottom: '12px', paddingLeft: '16px', fontWeight: '600', color: '#2c5282' }}>
              UIC: MAJU101152000
            </p>
            <p style={{ marginBottom: '12px' }}>
              If your mother's or father's first name is <strong>not available</strong>, the missing name component is replaced with your initials:
            </p>
            <p style={{ marginBottom: '12px', fontStyle: 'italic', paddingLeft: '16px' }}>
              <strong>First letter of your first name + First letter of your last name</strong>
            </p>
            <p style={{ marginBottom: '0' }}>
              The UIC helps healthcare providers <strong>identify and manage your testing records while reducing the need to display your full name</strong>. Your HIV testing information should still be handled with appropriate confidentiality and privacy safeguards.
            </p>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default AccountInformation;


